import React, { useState } from "react";
import { ParsedResume } from "./ExternalResumeUploadDialog";
import HttpHelper from "../../utils/HttpHelper";
import { useParams } from "react-router-dom";
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Chip,
  Stack,
  Typography,
} from "@mui/material";

interface Props {
  resumes: ParsedResume[];
  setResumes: React.Dispatch<React.SetStateAction<ParsedResume[]>>;
  jobIdProp?: string;
}

const ResumePreviewTable: React.FC<Props> = ({
  resumes,
  setResumes,
  jobIdProp,
}) => {
  const { jobId: jobIdFromParams } = useParams<{ jobId: string }>();
  const jobId = jobIdProp || jobIdFromParams;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempName, setTempName] = useState("");
  const [tempEmail, setTempEmail] = useState("");

  const handleCreate = async (resume: ParsedResume) => {
    if (!jobId) return;

    // If currently editing this resume, use the live temp values
    const name = editingId === resume.id ? tempName : resume.name;
    const email = editingId === resume.id ? tempEmail : resume.email;

    // If we were editing, save edits and clear editing state first
    if (editingId === resume.id) {
      if (blurTimeout.current) {
        clearTimeout(blurTimeout.current);
        blurTimeout.current = null;
      }
      saveEdits(resume.id);
    }

    if (!name.trim()) {
      alert("Name cannot be empty.");
      return;
    }

    if (!email.trim()) {
      alert("Email cannot be empty.");
      return;
    }

    const [firstName, ...rest] = name.split(" ");
    const lastName = rest.join(" ") || null;

    try {
      const response = await HttpHelper.post(
        `/hr/jobs/${jobId}/create-external-applicant`,
        {
          firstName: firstName || null,
          lastName,
          email,
          phone: null,
        }
      );

      setResumes(prev =>
        prev.map(r =>
          r.id === resume.id
            ? {
              ...r,
              status: response.duplicate ? "duplicate" : "created",
            }
            : r
        )
      );
    } catch {
      setResumes(prev =>
        prev.map(r =>
          r.id === resume.id ? { ...r, status: "error" } : r
        )
      );
    }
  };

  const saveEdits = (id: string) => {
    setResumes(prev =>
      prev.map(r =>
        r.id === id
          ? { ...r, name: tempName, email: tempEmail }
          : r
      )
    );
    setEditingId(null);
  };

  // Use a timeout so clicking between name/email fields doesn't close edit mode
  const blurTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBlur = (id: string) => {
    blurTimeout.current = setTimeout(() => {
      saveEdits(id);
    }, 150);
  };

  const handleFocus = () => {
    if (blurTimeout.current) {
      clearTimeout(blurTimeout.current);
      blurTimeout.current = null;
    }
  };

  return (
    <Paper elevation={3} sx={{ mt: 2, p: 1 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Resume Preview
      </Typography>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>File</strong></TableCell>
            <TableCell><strong>Name</strong></TableCell>
            <TableCell><strong>Email</strong></TableCell>
            <TableCell><strong>Action</strong></TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {resumes.map((resume) => (
            <TableRow key={resume.id} hover>

              {/* FILE */}
              <TableCell>{resume.file.name}</TableCell>

              {/* NAME */}
              <TableCell>
                {editingId === resume.id ? (
                  <TextField
                    size="small"
                    fullWidth
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    onBlur={() => handleBlur(resume.id)}
                    onFocus={handleFocus}
                    autoFocus
                  />
                ) : (
                  <Typography
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setEditingId(resume.id);
                      setTempName(resume.name);
                      setTempEmail(resume.email);
                    }}
                  >
                    {resume.name || "-"}
                  </Typography>
                )}
              </TableCell>

              {/* EMAIL */}
              <TableCell>
                {editingId === resume.id ? (
                  <TextField
                    size="small"
                    fullWidth
                    value={tempEmail}
                    onChange={(e) => setTempEmail(e.target.value)}
                    onBlur={() => handleBlur(resume.id)}
                    onFocus={handleFocus}
                  />
                ) : (
                  <Typography
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setEditingId(resume.id);
                      setTempName(resume.name);
                      setTempEmail(resume.email);
                    }}
                  >
                    {resume.email || "-"}
                  </Typography>
                )}
              </TableCell>

              {/* ACTION */}
              <TableCell>
                {resume.status === "processing" && (
                  <Chip label="Processing..." color="warning" />
                )}

                {resume.status === "error" && (
                  <Chip label="Error" color="error" />
                )}

                {resume.status === "parsed" && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleCreate(resume)}
                  >
                    Create Applicant
                  </Button>
                )}

                {resume.status === "created" && (
                  <Chip label="Created" color="success" />
                )}

                {resume.status === "duplicate" && (
                  <Chip label="Duplicate" color="info" />
                )}
              </TableCell>

            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default ResumePreviewTable;